import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  ShoppingBag,
  Globe,
  History,
  LayoutDashboard,
  Menu,
  X,
  LogOut,
  ChevronDown,
  ChevronUp,
  Target,
  Users,
  BarChart3,
  Home,
  LogIn,
  KeyRound,
  ShieldCheck,
  UserCog,
  TrendingUp,
  RefreshCw
} from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sales } from './Sales';
import { SalesHistory } from './SalesHistory';
import { SalesOnline } from './SalesOnline';
import { useAuth } from '../contexts/AuthContext';
import { api, API_BASE_URL } from '../services/api';
import { structuredMenu } from '../components/Layout';

export const SalesManager: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'pos' | 'history' | 'online'>('pos');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [allowedNavigation, setAllowedNavigation] = useState<any[]>([]);

  const { user, logout, loading: authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // ==================== SESSÃO DO OPERADOR (PIN) ====================
  // Inicializado de forma síncrona a partir do localStorage para que, após um refresh,
  // os dados (total do dia, metas, progresso) pertençam imediatamente ao operador PIN
  // e não sejam calculados primeiro com os dados da empresa inteira.
  const [currentOperator, setCurrentOperator] = useState<any>(() => {
    try {
      const saved = localStorage.getItem('pos_operator');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [operatorsList, setOperatorsList] = useState<any[]>([]);
  const [showOperatorSelect, setShowOperatorSelect] = useState(false);
  const [pendingOperatorId, setPendingOperatorId] = useState<string | null>(null);
  const [showOperatorPinModal, setShowOperatorPinModal] = useState(false);
  const [operatorPinInput, setOperatorPinInput] = useState(['', '', '', '']);
  const [operatorPinError, setOperatorPinError] = useState('');

  // Estatísticas do dia + metas (header)
  const [todayStats, setTodayStats] = useState<{ count: number; total: number } | null>(null);
  const [dailyGoal, setDailyGoal] = useState<{ target: number; progress: number; dailyTarget?: number; monthTarget?: number } | null>(null);
  const [refreshSignal, setRefreshSignal] = useState(0);

  const loadTodayData = useCallback(async (operator?: any | null) => {
    try {
      const today = new Date();
      const dateStr = today.toISOString().split('T')[0];
      const operatorId = operator?._id;
      const filters: any = { page: 1, limit: 200, startDate: dateStr, endDate: dateStr };
      if (operatorId) filters.sellerId = operatorId;
      const salesRes = await api.sales.getAll(filters);
      const salesData = salesRes?.sales || salesRes || [];
      const list = Array.isArray(salesData) ? salesData : [];
      // Fecho de caixa zera a produção diária: após um fecho, só contam vendas posteriores ao fecho
      let effectiveList = list;
      const isOwnOperator = !operatorId || String(operatorId) === String(user?._id);
      if (isOwnOperator) {
        try {
          const closures = await api.cashClosures.getMine();
          const dayClosures = (Array.isArray(closures) ? closures : [])
            .filter(c => c && c.date && c.createdAt && new Date(c.date).toISOString().split('T')[0] === dateStr);
          if (dayClosures.length) {
            const latest = dayClosures.reduce((a: any, b: any) => (new Date(a.createdAt) > new Date(b.createdAt) ? a : b));
            effectiveList = list.filter(s => new Date(s.createdAt) > new Date(latest.createdAt));
          }
        } catch {
          // sem fechos — mantém a lista completa
        }
      }
      const count = effectiveList.length;
      const total = effectiveList.reduce((s: number, sale: any) => s + (sale.total || 0), 0);
      setTodayStats({ count, total });

      // 1. Meta explicitamente activa (Company.activeGoal, definida na tela Metas)
      let activeGoal: any = null;
      try {
        activeGoal = await api.goals.getActive();
      } catch {
        activeGoal = null;
      }
      // 2. Fallback legado: meta cujo período cobre hoje, no ano corrente
      if (!activeGoal) {
        const goals = await api.goals.getAll(today.getFullYear());
        activeGoal = (Array.isArray(goals) ? goals : []).find((g: any) => {
          if (!g.startDate || !g.endDate) return false;
          const start = new Date(g.startDate);
          const end = new Date(g.endDate);
          return today >= start && today <= end;
        }) || null;
      }
      if (activeGoal) {
        const financialTarget = activeGoal.financialTarget || activeGoal.annualTarget || 0;
        let monthTarget = activeGoal.period === 'monthly' ? financialTarget : (activeGoal.monthlyTarget || 0);

        // Meta individual do utilizador/operador, extraída da distribuição criada na tela Metas
        try {
          const distributions = await api.goals.getDistributions(activeGoal._id);
          const dists = Array.isArray(distributions) ? distributions : [];
          if (dists.length) {
            const currentUserId = operatorId || user?._id;
            const operatorRole = operator?.role && typeof operator.role === 'object' ? operator.role : null;
            const userRole = user?.role && typeof user.role === 'object' ? user.role : null;
            const roleId = operatorRole?._id || userRole?._id;
            const roleName = operatorRole?.roleName || userRole?.roleName || (typeof user?.role === 'string' ? user.role : null);

            const myDist = dists.find((d: any) => {
              const assignedId = d.assignedUser && typeof d.assignedUser === 'object' ? d.assignedUser._id : d.assignedUser;
              if (currentUserId && assignedId && String(assignedId) === String(currentUserId)) return true;
              const dRoleId = d.role && typeof d.role === 'object' ? d.role._id : d.role;
              if (roleId && dRoleId && String(dRoleId) === String(roleId)) return true;
              const dRoleName = d.role && typeof d.role === 'object' ? d.role.roleName : null;
              if (roleName && dRoleName && dRoleName.toLowerCase() === roleName.toLowerCase()) return true;
              return false;
            });

            if (myDist) {
              const curMonth = today.getMonth() + 1;
              const mb = (myDist.monthlyBreakdown || []).find((m: any) => Number(m.month) === curMonth);
              const distMonth = mb?.target || ((myDist.annualTarget || 0) / 12);
              if (distMonth > 0) monthTarget = distMonth;
            }
          }
        } catch {
          // sem distribuição disponível — mantém o target da meta da empresa
        }

        const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
        const dailyTarget = monthTarget > 0 ? monthTarget / daysInMonth : 0;
        setDailyGoal({
          target: dailyTarget > 0 ? dailyTarget : financialTarget,
          progress: total,
          dailyTarget: dailyTarget > 0 ? dailyTarget : financialTarget,
          monthTarget: monthTarget > 0 ? monthTarget : financialTarget,
        });
      }
    } catch {
      // silent — stats são não-críticas
    }
  }, [user]);

  // Restore operator from localStorage on mount (agora feito sincronamente no useState acima)
  useEffect(() => {
    loadTodayData(currentOperator);
  }, [currentOperator, loadTodayData]);

  const loadOperators = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return;
      const res = await fetch(`${API_BASE_URL}/users/operators`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setOperatorsList(data);
      }
    } catch {}
  }, []);

  const handleOperatorPinDigit = (idx: number, val: string) => {
    if (!/^[0-9]$/.test(val) && val !== '') return;
    setOperatorPinError('');
    const next = [...operatorPinInput];
    next[idx] = val;
    setOperatorPinInput(next);
    if (val !== '' && idx < 3) {
      const nextInput = document.getElementById(`op-pin-${idx + 1}`);
      nextInput?.focus();
    }
  };

  const handleOperatorKeyDown = (idx: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && operatorPinInput[idx] === '' && idx > 0) {
      const prevInput = document.getElementById(`op-pin-${idx - 1}`);
      prevInput?.focus();
      const next = [...operatorPinInput];
      next[idx - 1] = '';
      setOperatorPinInput(next);
    }
  };

  const handleOperatorPinSubmit = async () => {
    const pin = operatorPinInput.join('');
    if (pin.length !== 4) { setOperatorPinError('Digite o PIN de 4 dígitos'); return; }
    try {
      const token = localStorage.getItem('accessToken');
      const body: any = { pin };
      if (pendingOperatorId) body.operatorId = pendingOperatorId;
      const res = await fetch(`${API_BASE_URL}/users/pin-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const err = await res.json();
        setOperatorPinError(err.message || 'PIN inválido');
        setOperatorPinInput(['', '', '', '']);
        return;
      }
      const data = await res.json();
      setCurrentOperator(data.operator);
      localStorage.setItem('pos_operator', JSON.stringify(data.operator));
      setShowOperatorPinModal(false);
      setOperatorPinInput(['', '', '', '']);
      setOperatorPinError('');
      setPendingOperatorId(null);
    } catch {
      setOperatorPinError('Erro de rede. Tente novamente.');
    }
  };

  const handleOperatorSelect = (op: any) => {
    setPendingOperatorId(op._id);
    setOperatorPinInput(['', '', '', '']);
    setOperatorPinError('');
    setShowOperatorSelect(false);
    setShowOperatorPinModal(true);
  };

  const handleOperatorLogout = () => {
    setCurrentOperator(null);
    localStorage.removeItem('pos_operator');
  };

  // Fecha o menu ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // ==================== PERMISSÕES DO DRAWER ====================
  useEffect(() => {
    if (!user || authLoading) {
      setAllowedNavigation([{ name: 'Dashboard', href: '/dashboard', icon: Home }]);
      return;
    }

    const roleName = user.role?.roleName || user.role || '';

    if (roleName === 'referralPartner') {
      const referralNavigation = [
        { name: 'Dashboard de Recomendação', href: '/referral/dashboard', icon: Target },
        { name: 'Recomendar Clientes', href: '/recommended-clients', icon: Users },
        { name: 'Meus ganhos', href: '/my-earnings', icon: BarChart3 },
      ];
      setAllowedNavigation(referralNavigation);
      return;
    }

    const loadPermissions = () => {
      const rolePermissions: string[] = user.rolePermissions ||
                                       user.role?.allowedMenuItems ||
                                       user.role?.permissions || [];

      const allAllowedItems = new Set(
        structuredMenu.flatMap(section =>
          section.items.filter(item =>
            item.name === 'Dashboard' || rolePermissions.includes(item.name)
          )
        )
      );

      setAllowedNavigation(Array.from(allAllowedItems));
    };

    loadPermissions();
    const handleUpdate = () => loadPermissions();
    window.addEventListener('userPermissionsUpdated', handleUpdate);
    return () => window.removeEventListener('userPermissionsUpdated', handleUpdate);
  }, [user, authLoading]);

  // Fecha o drawer ao mudar de rota
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSection = (sectionId: string) => {
    setOpenSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const visibleSections = structuredMenu.map(section => ({
    ...section,
    items: section.items.filter(item =>
      allowedNavigation.some(allowed => allowed.name === item.name)
    )
  })).filter(section => section.items.length > 0);

  // Ícone e texto da aba ativa (para o FAB)
  const getActiveTabInfo = () => {
    switch (activeTab) {
      case 'pos':
        return { icon: ShoppingBag, label: 'PDV' };
      case 'history':
        return { icon: History, label: 'His' };
      case 'online':
        return { icon: Globe, label: 'On' };
    }
  };

  const { icon: ActiveIcon, label: activeLabel } = getActiveTabInfo();

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Cabeçalho fixo + navegação desktop */}
      <div className="bg-white border-b border-gray-100 px-4 sm:px-6 py-2 md:py-6 top-0 z-10">
        <div className="max-w-[1440px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Hamburger — traz de volta o menu da app (sem sidebar no POS) */}
            <button
              onClick={() => setDrawerOpen(true)}
              title="Abrir menu"
              className="flex items-center justify-center w-11 h-11 rounded-xl border border-gray-200 bg-white text-gray-700 shadow-sm hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-200 transition-all active:scale-95"
            >
              <Menu size={22} />
            </button>

            <h1 className="text-md sm:text-md font-black text-gray-900 tracking-tight uppercase">
              POS
            </h1>

            {/* Total de hoje + meta diária + meta do mês (só desktop) */}
            <div className="hidden lg:flex items-center gap-2 ml-2">
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-100 rounded-full px-3 py-1.5" title="Valor feito até agora">
                <TrendingUp size={13} className="text-emerald-500" />
                <span className="text-xs font-black text-gray-900 whitespace-nowrap">
                  {(todayStats?.total || 0).toLocaleString()} MT
                </span>
              </div>
              <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-100 rounded-full px-3 py-1.5" title="Meta diária">
                <Target size={13} className="text-indigo-500" />
                <span className="text-[11px] font-bold text-indigo-700 whitespace-nowrap">
                  {dailyGoal?.dailyTarget ? `${Math.round(dailyGoal.dailyTarget).toLocaleString()} MT/dia` : '—'}
                </span>
              </div>
              <div className="flex items-center gap-2 bg-violet-50 border border-violet-100 rounded-full px-3 py-1.5" title="Meta do mês">
                <BarChart3 size={13} className="text-violet-500" />
                <span className="text-[11px] font-bold text-violet-700 whitespace-nowrap">
                  {dailyGoal?.monthTarget ? `${Math.round(dailyGoal.monthTarget).toLocaleString()} MT/mês` : '—'}
                </span>
              </div>
              {/* Progresso circular da meta diária */}
              {dailyGoal && dailyGoal.dailyTarget > 0 && (
                <div className="relative w-9 h-9" title={`${Math.round((dailyGoal.progress / dailyGoal.dailyTarget) * 100)}% da meta diária`}>
                  <svg viewBox="0 0 36 36" className="w-9 h-9 -rotate-90">
                    <circle cx="18" cy="18" r="15.5" fill="none" stroke="#e5e7eb" strokeWidth="4" />
                    <circle
                      cx="18" cy="18" r="15.5" fill="none"
                      stroke={dailyGoal.progress >= dailyGoal.dailyTarget ? '#10b981' : '#6366f1'}
                      strokeWidth="4" strokeLinecap="round"
                      strokeDasharray="97.4"
                      strokeDashoffset={97.4 * (1 - Math.min(1, dailyGoal.progress / dailyGoal.dailyTarget))}
                    />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-[8px] font-black text-indigo-600">
                    {Math.round((dailyGoal.progress / dailyGoal.dailyTarget) * 100)}%
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {/* Navegação desktop – visível apenas em md+ */}
            <nav className="hidden md:flex bg-gray-100 p-1.5 rounded-[20px] gap-1.5">
              <button
                onClick={() => setActiveTab('pos')}
                className={`flex items-center gap-2.5 px-5 py-2.5 rounded-[16px] text-sm font-semibold transition-all ${
                  activeTab === 'pos'
                    ? 'bg-white text-blue-600 shadow-sm ring-1 ring-black/5'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-white/60'
                }`}
              >
                <ShoppingBag size={18} />
                Terminal PDV
              </button>

              <button
                onClick={() => setActiveTab('history')}
                className={`flex items-center gap-2.5 px-5 py-2.5 rounded-[16px] text-sm font-semibold transition-all ${
                  activeTab === 'history'
                    ? 'bg-white text-blue-600 shadow-sm ring-1 ring-black/5'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-white/60'
                }`}
              >
                <History size={18} />
                Histórico
              </button>

              <button
                onClick={() => setActiveTab('online')}
                className={`flex items-center gap-2.5 px-5 py-2.5 rounded-[16px] text-sm font-semibold transition-all ${
                  activeTab === 'online'
                    ? 'bg-white text-blue-600 shadow-sm ring-1 ring-black/5'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-white/60'
                }`}
              >
                <Globe size={18} />
                Vendas Online
              </button>
            </nav>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {/* Operador — login com PIN ao lado do logout */}
            <div className="relative">
              <button onClick={() => { loadOperators(); setShowOperatorSelect(true); }}
                className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 border border-indigo-100 hover:border-indigo-200 transition-all active:scale-95"
                title="Trocar operador"
              >
                {currentOperator ? (
                  <>
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center">
                      <UserCog size={13} className="text-white" />
                    </div>
                    <span className="hidden md:inline text-gray-800 font-semibold">{currentOperator.firstName}</span>
                    <ChevronDown size={14} className="text-gray-400 hidden md:inline" />
                  </>
                ) : (
                  <>
                    <LogIn size={16} />
                    <span className="hidden md:inline">Entrar</span>
                  </>
                )}
              </button>

              {showOperatorSelect && (
                <div className="absolute top-full mt-2 right-0 z-50 bg-white rounded-2xl shadow-2xl border border-gray-100 p-2 w-64 max-h-72 overflow-y-auto"
                  onMouseLeave={() => setShowOperatorSelect(false)}
                >
                  <p className="text-[10px] font-bold text-gray-400 uppercase px-3 py-2">Operadores</p>
                  {operatorsList.map((op: any) => (
                    <button key={op._id} onClick={() => handleOperatorSelect(op)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition ${
                        currentOperator?._id === op._id ? 'bg-indigo-50 text-indigo-700' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 to-violet-400 flex items-center justify-center">
                        <UserCog size={14} className="text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-gray-800 truncate">{op.firstName} {op.lastName}</p>
                        <p className="text-[10px] text-gray-400 truncate">{op.role?.roleName || 'Operador'}{op.pinCode ? ' • PIN ✓' : ''}</p>
                      </div>
                      {currentOperator?._id === op._id && <ShieldCheck size={16} className="text-green-500 flex-shrink-0" />}
                    </button>
                  ))}
                  <div className="border-t border-gray-100 mt-2 pt-2">
                    <button onClick={() => { setShowOperatorSelect(false); setPendingOperatorId(null); setShowOperatorPinModal(true); }}
                      className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm text-indigo-600 font-semibold hover:bg-indigo-50 transition"
                    >
                      <KeyRound size={14} /> Entrar com PIN
                    </button>
                    {currentOperator && (
                      <button onClick={() => { handleOperatorLogout(); setShowOperatorSelect(false); }}
                        className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm text-red-500 font-semibold hover:bg-red-50 transition"
                      >
                        <LogIn size={14} /> Sair
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Refresh — atualiza vendas de hoje + catálogo + clientes em tempo real */}
            <button
              onClick={() => { loadTodayData(currentOperator); setRefreshSignal(s => s + 1); }}
              title="Atualizar dados"
              className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold text-gray-600 bg-gray-50 hover:bg-gray-100 border border-gray-100 hover:border-gray-200 transition-all active:scale-95"
            >
              <RefreshCw size={18} />
            </button>

            {/* Logout — sair do app */}
            <button
              onClick={handleLogout}
              title="Sair do app"
              className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-100 hover:border-rose-200 transition-all active:scale-95"
            >
              <LogOut size={18} />
              <span className="hidden sm:inline">Sair</span>
            </button>
          </div>
        </div>
      </div>

      {/* Floating Action Button + Menu Mobile */}
      <div className="md:hidden fixed bottom-24 right-6 z-40">
        <div className="relative" ref={menuRef}>
          {/* Botão principal */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="flex items-center justify-center w-16 h-16 bg-blue-600 text-white rounded-full shadow-2xl hover:bg-blue-700 transition-all active:scale-95"
          >
            {isMobileMenuOpen ? (
              <X size={28} />
            ) : (
              <div className="flex flex-col items-center text-xs font-bold">
                <ActiveIcon size={26} />
                <span className="mt-1">{activeLabel}</span>
              </div>
            )}
          </button>

          {/* Menu dropdown */}
          {isMobileMenuOpen && (
            <div className="absolute bottom-full right-0 mb-4 w-64 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden animate-in fade-in slide-in-from-bottom-5 duration-200">
              <div className="py-2">
                <button
                  onClick={() => {
                    setActiveTab('pos');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-gray-50 transition ${
                    activeTab === 'pos' ? 'bg-blue-50 text-blue-700 font-semibold' : ''
                  }`}
                >
                  <ShoppingBag size={22} />
                  <span>Terminal PDV</span>
                </button>

                <button
                  onClick={() => {
                    setActiveTab('history');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-gray-50 transition ${
                    activeTab === 'history' ? 'bg-blue-50 text-blue-700 font-semibold' : ''
                  }`}
                >
                  <History size={22} />
                  <span>Histórico & Filtros</span>
                </button>

                <button
                  onClick={() => {
                    setActiveTab('online');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-gray-50 transition ${
                    activeTab === 'online' ? 'bg-blue-50 text-blue-700 font-semibold' : ''
                  }`}
                >
                  <Globe size={22} />
                  <span>Vendas Online</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Conteúdo principal */}
      <main className="flex-1 overflow-auto pb-24 md:pb-0">
        <div className="animate-in fade-in zoom-in-95 duration-300 h-full">
          {activeTab === 'pos' && (
            <Sales
              currentOperator={currentOperator}
              todayStats={todayStats}
              dailyGoal={dailyGoal}
              onSaleComplete={() => loadTodayData(currentOperator)}
              refreshSignal={refreshSignal}
            />
          )}
          {activeTab === 'history' && (
            <div className="py-6 px-4 sm:px-6">
              <SalesHistory />
            </div>
          )}
          {activeTab === 'online' && (
            <div className="py-6 px-4 sm:px-6">
              <SalesOnline />
            </div>
          )}
        </div>
      </main>

      {/* ==================== DRAWER MENU (HAMBURGER) ==================== */}
      <AnimatePresence>
        {drawerOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setDrawerOpen(false)}
              className="fixed inset-0 bg-black/50 z-40"
            />

            {/* Drawer */}
            <motion.aside
              initial={{ x: -320 }}
              animate={{ x: 0 }}
              exit={{ x: -320 }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              className="fixed left-0 top-0 bottom-0 w-[300px] sm:w-[320px] bg-white z-50 flex flex-col shadow-2xl"
            >
              {/* Header do drawer */}
              <div className="h-20 flex items-center justify-between px-5 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <img
                    src="https://meupontodevenda.com/cdn/icon-logo.svg"
                    alt="Meu Ponto de Venda"
                    className="w-9 h-9 object-contain"
                  />
                  <div>
                    <p className="font-black text-gray-900 leading-tight">Meu Ponto de Venda</p>
                    <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Menu da app</p>
                  </div>
                </div>
                <button
                  onClick={() => setDrawerOpen(false)}
                  className="p-2 rounded-lg text-gray-500 hover:bg-slate-100 hover:text-gray-700 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Navegação */}
              <nav className="flex-1 px-3 space-y-1 overflow-y-auto py-4">
                {visibleSections.map((section) => {
                  const isOpen = openSections[section.id] ?? true;
                  const SectionIcon = section.icon;

                  return (
                    <div key={section.id} className="mb-1">
                      <button
                        onClick={() => toggleSection(section.id)}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold hover:bg-slate-100 transition-colors group"
                      >
                        <SectionIcon className="h-5 w-5 text-slate-500" />
                        <span className="flex-1 text-left">{section.title}</span>
                        {isOpen
                          ? <ChevronUp size={18} className="text-slate-400" />
                          : <ChevronDown size={18} className="text-slate-400" />}
                      </button>

                      <AnimatePresence initial={false}>
                        {isOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.22 }}
                            className="overflow-hidden pl-3 mt-1 space-y-0.5"
                          >
                            {section.items.map((item) => {
                              const ItemIcon = item.icon;
                              const isActive = location.pathname === item.href;
                              return (
                                <Link
                                  key={item.name}
                                  to={item.href}
                                  onClick={() => setDrawerOpen(false)}
                                  className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 text-sm ${
                                    isActive
                                      ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20'
                                      : 'text-gray-600 hover:bg-slate-100 hover:text-slate-900'
                                  }`}
                                >
                                  <ItemIcon className="h-4 w-4 shrink-0" />
                                  <span className="font-medium">{item.name}</span>
                                </Link>
                              );
                            })}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </nav>

              {/* Footer do drawer */}
              <div className="border-t border-slate-100 p-4">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 transition-colors"
                >
                  <LogOut size={18} />
                  Terminar sessão
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Modal PIN do Operador */}
      {showOperatorPinModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4" onClick={() => { setShowOperatorPinModal(false); setOperatorPinInput(['', '', '', '']); setOperatorPinError(''); setPendingOperatorId(null); }}>
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-8 text-center" onClick={e => e.stopPropagation()}>
            <div className="mx-auto w-14 h-14 bg-gradient-to-br from-indigo-500 to-violet-500 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
              <KeyRound size={28} className="text-white" />
            </div>
            <h3 className="text-xl font-black text-gray-900 mb-1">{pendingOperatorId ? 'Verificar Operador' : 'Entrar como Operador'}</h3>
            <p className="text-sm text-gray-500 mb-6">{pendingOperatorId ? 'Confirme o PIN do operador selecionado' : 'Digite o PIN de 4 dígitos'}</p>

            <div className="flex justify-center gap-3 mb-6">
              {[0, 1, 2, 3].map(i => (
                <input
                  key={i}
                  id={`op-pin-${i}`}
                  type="password"
                  maxLength={1}
                  value={operatorPinInput[i]}
                  onChange={e => handleOperatorPinDigit(i, e.target.value)}
                  onKeyDown={e => handleOperatorKeyDown(i, e)}
                  className={`w-14 h-16 text-center text-2xl font-black border-2 rounded-xl outline-none transition ${
                    operatorPinError ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-indigo-500 bg-gray-50'
                  }`}
                  autoFocus={i === 0}
                  inputMode="numeric"
                />
              ))}
            </div>

            {operatorPinError && (
              <p className="text-sm text-red-500 font-semibold mb-4">{operatorPinError}</p>
            )}

            <div className="flex gap-3">
              <button onClick={() => { setShowOperatorPinModal(false); setOperatorPinInput(['', '', '', '']); setOperatorPinError(''); setPendingOperatorId(null); }}
                className="flex-1 py-3 px-4 bg-gray-100 text-gray-600 rounded-xl font-semibold hover:bg-gray-200 transition"
              >
                Cancelar
              </button>
              <button onClick={handleOperatorPinSubmit}
                disabled={operatorPinInput.join('').length !== 4}
                className="flex-1 py-3 px-4 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <ShieldCheck size={16} /> Entrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
